import React, { useState, useEffect } from 'react';
import { Helmet } from 'rnd-helmet';
import { injectIntl } from 'react-intl';
import {Redirect, Route, useRouteMatch} from "react-router-dom";
import ViewSwitcher from "@Components/ViewSwitcher";
import axios from "axios";
import BranchListItem from "@Components/BranchListItem";
import Map from "@Components/Map";
import { List } from "react-content-loader";
import dispatchRerender from "dispatchRerender";
import GetRouteFromUrl from "GetRouteFromUrl";
import searchParamsParser from "@Utils/searchParamsParser";
import { generateDataLayer } from "dataLayerHelper";
import stringSanitizer from "stringSanitizer";
import Breadcrumbs from "@Components/Breadcrumbs";
import SearchForm from "@Filters/SearchForm";
import searchParamsStringify from "@Utils/searchParamsStringify";
import Tabs from "@Components/Tabs";
import officesRegionRawData from "@Config/fr/officeRegion.json"
import LinkList from "@Components/LinkList";
import ModalList from "@Components/ModalList";
import PagerShowMore from "../../../components/PagerShowMore/fr";
import scrollTo from "scrollTo";
import Optimizely from '@TrackingComponents/Optimizely';

function Branches({ intl, staticContext, match, location }) {
  const { locale } = intl;
  let initialData = {
    hideElements: false
  };
  let searchParams = {};
  const route = useRouteMatch();
  const routeConfig = GetRouteFromUrl();
  if (match.params.searchParams) {
    searchParams = searchParamsParser(match.params.searchParams);
  }
  if (staticContext && staticContext.data) {
    initialData = staticContext.data;
    // Pass the dataLayer to the server using staticContext.
    if (staticContext.data.appConfig && staticContext.data.appConfig.gtm) {
      const gtm = staticContext.data.appConfig.gtm;
      if (gtm.hasOwnProperty("enabled") && gtm.enabled) {
        staticContext.dataLayer = generateDataLayer("office_locator_list", locale);
      }
    }
  }
  else if (typeof window !== "undefined" && window.__ROUTE_DATA__) {
    initialData = window.__ROUTE_DATA__;
    initialData.hideElements = false;
  }

  if (Object.keys(searchParams).length) {
    initialData.hideElements = true;
  }

  let optimizelySettings;
  if (typeof window !== 'undefined' && window.__OPTIMIZELY_SETTINGS__) {
    optimizelySettings = window.__OPTIMIZELY_SETTINGS__;
  } else {
    optimizelySettings = staticContext?.optimizelySettings || {};
  }

  const [state, setState] = useState(initialData)
  const [pages, setPages] = useState([]);
  let currentPage = searchParams && searchParams.page ? parseInt(searchParams.page) : 1;
  const itemsPerPage = state.appConfig && state.appConfig.elements_per_page ? parseInt(state.appConfig.elements_per_page) : parseInt(process.env.REACT_APP_SEARCH_ITEMS_PER_PAGE);
  const [showMap, setShowMap] = useState(false)
  const [modalData, setModalData] = useState({})
  const [activeTab, setActiveTab] = useState("métropole")
  const [loading, setLoading] = useState(true)
  const [loadingResult, setLoadingResult] = useState(false)
  const [startingPage] = useState(parseInt(initialData.startingPage));
  const noSearchParams = Object.keys(searchParams).length === 0 || (Object.keys(searchParams).length === 1 && typeof searchParams.type !== "undefined") || (Object.keys(searchParams).length === 2 && typeof searchParams.type !== "undefined" && typeof searchParams.page !== "undefined")
  const noResults = !!state.searchParams.noResults;
  const [defaultPage, setDefaultPage] = useState(noSearchParams || noResults);

  const locationIsValid = state.geoData && Object.keys(state.geoData).length !== 0;

  if (staticContext && !locationIsValid && noResults) {
    staticContext.status = 404;
  }

  useEffect(() => {
    document.querySelector('body').classList.add('has-filter-bar');
    // Setup scrolling functionality.
    const body = document.getElementsByTagName("body");

    if (body.length) {
      body[0].classList.add("loaded");
    }

    if (searchParams.page) {
      scrollTo(`#page-${searchParams.page - 1}`);
    }
  }, [])

  useEffect(() => {
    setShowMap(searchParams.type === "map")
  }, [searchParams.type])

  useEffect(() => {
    setLoading(true);
    getSearchResult()
  }, [match.params.searchParams, location.search, searchParams.page]);

  function setFocusOnFirstResultItem() {
    // Get last child from the loaded page and skip first page (this case is valid on page load).
    const firstItem = document.querySelector('.cards > div:last-child:not(:first-child) .cards__item a')
    if (firstItem) {
      firstItem.focus();
    }
  }

  function modalDataSetter(id, branchData) {
    const data = {
      id,
      branchData
    }

    setModalData(data)
  }

  function onModalClose() {
    setModalData({})
  }

  let getSearchResult = () => {
    const callbackAlias = '@Callbacks/getSearchResults';
    const data = {
      language: intl.locale,
    };
    axios.post('/api/branches/get-callback', {
      callback: callbackAlias,
      currentRoute: { ...match, ...{ routeName: "our-offices" }, ...{ params: { searchParams: searchParamsStringify(searchParams) } } },
      data: data
    }).then(response => {
      let stateObj = response.data;
      if (Object.keys(searchParams).length) {
        stateObj = {
          ... stateObj,
          hideElements: true
        }
      }

      setState(stateObj);
      setLoading(false);
      setLoadingResult(false)

      if (response.data.searchResults && response.data.searchResults.hits && response.data.searchResults.hits.hits.length > 0) {
        setPages((prev) => {
          if (searchParams.page && searchParams.page > 1) {
            return [
              ...prev,
              ...response.data.searchResults.hits.hits
            ];
          }
          else {
            return [
              ...response.data.searchResults.hits.hits
            ];
          }
        })
      }

      dispatchRerender();
      // Set focus continue from the next page first result item after load.
      setFocusOnFirstResultItem();
    })
  }

  if (typeof window !== "undefined") {
    window.onscroll = event => {
      const body = document.querySelector("body.loaded");

      if (!body) {
        return false;
      }

      const pagesIds = document.querySelectorAll('[id*="page-"]');
      for (let id in pagesIds) {
        const page = pagesIds[id];

        if (isElementVisible(page)) {
          let pageNum = page.id.replace('page-', '');
          let searchFilters = { ...searchParams };

          if (pageNum <= 1) {
            searchFilters.page = undefined;
          } else {
            searchFilters.page = pageNum;
          }

          window.history.replaceState(null, '', `${match.path.replace(':searchParams*', '')}${searchParamsStringify(searchFilters)}`);
        }
      }
    }

    const isElementVisible = (element) => {
      const hidden = element.hidden;

      if (hidden) {
        element.hidden = false;
      }

      const elementTop = element.offsetTop;
      const elementBottom = elementTop + element.offsetHeight;
      const viewportTop = window.pageYOffset;
      const viewportBottom = viewportTop + window.innerHeight;

      if (hidden) {
        element.hidden = true;
      }

      return elementBottom > viewportTop && elementTop < viewportBottom;
    }
  }

  let baseUrl = route.path.replace(':searchParams*', '');
  if (searchParams && searchParams.page && parseInt(searchParams.page) === 1) {
    return <Route render={({staticContext}) => {
      if (staticContext) staticContext.status = 301;
      return <Redirect to={`${baseUrl}${searchParamsStringify({...searchParams, ...{page: null}})}`}/>
    }}/>
  }

  let pager = {};
  let modalPager = {};
  let total = loading ? null : state.searchResults.hits.total;
  let pagerTotal = 0;
  let noResultsText = "";
  let regionData = {...officesRegionRawData}

  const getContentView = () => {
    if (showMap) {
      return null;
    }

    // Show list link component on the default page.
    if (defaultPage || noResults) {
      return <LinkList />
    }

    pagerTotal = state.searchResults.hits.total;

    if (pages.length/itemsPerPage === currentPage) {
      currentPage = 1;
    }
    else if (startingPage && currentPage !== startingPage) {
      currentPage = startingPage;
    }

    for (let id in pages) {
      const branchData = pages[id]['_source'];
      let page = Math.floor(id / itemsPerPage) + currentPage ;

      if (!pager[page]) {
        pager[page] = [];
      }

      if (!modalPager[page]) {
        modalPager[page] = [];
      }

      const distance = Math.ceil(pages[id]['sort'])
      let modalLinkId = `branch-link-${branchData.tid}-${id}`;

      pager[page].push(<BranchListItem key={id} branchData={branchData} distance={distance} appConfigs={state.appConfig} branchDataLinkTitle={modalLinkId} modalDataSetter={modalDataSetter} />)
    }

    return (
      <>
        <div className="map__list block__content--full-on-s block__content--full-on-m bg-variant-brand-tertiary pt-m l:pt-l">
          <div className="cards">
            {
              Object.keys(pager).map((item) => {
                return (
                  <div key={item}>
                    <div className="section-separator section-separator--border" hidden={item <= 1} id={`page-${item}`}>
                      {
                        item > 1 ?
                          <span>{intl.formatMessage({id: "SearchListing.Page"}, {page: item})}</span> : null
                      }
                    </div>
                    <div className="cards__list cards__list--format-grid">
                      { pager[item] }
                    </div>
                  </div>
                );
              })
            }
          </div>
          {modalData.id && <ModalList title={modalData.branchData.title} id={modalData.id} branchData={modalData.branchData} onClose={onModalClose} />}
        </div>
        <PagerShowMore searchParams={searchParams} totalMatches={pagerTotal} itemsPerPage={itemsPerPage} loadingResult={loadingResult} setLoadingResult={setLoadingResult}/>
      </>
    )
  }

  // TODO: This needs refactoring!
  const getMap = () => {
    if (!showMap) {
      return null;
    }

    let branchCoord = {
      'lat': '',
      'long': '',
      'branchPins': []
    }
    let regionsCount = [];
    let region;
    regionData['métropole']['pins'] = []
    // Set total pins count per region.
    for (let id in pages) {
      const branchData = pages[id]['_source'];

      if (!branchData.hasOwnProperty('address_line2')) {
        continue;
      }

      region = branchData.address_line2[0].split(',').shift();
      if (!regionData.hasOwnProperty(region)){
        continue;
      }

      regionsCount.hasOwnProperty(region) ? regionsCount[region] ++ : regionsCount[region] = 1;
    }

    for (let id in pages) {
      const branchData = pages[id]['_source'];
      if (!defaultPage && !noResults) {
        branchCoord.lat = branchData.lat;
        branchCoord.long = branchData.lng;
        branchCoord.branchPins.push(branchData)
      }

      if (branchData.hasOwnProperty('address_line2')) {
        region = branchData.address_line2[0].split(',').shift();
      }

      // Check if total pins count reached.
      if (regionData['métropole']['pins'].length < total) {
        regionData['métropole']['pins'].push(branchData)
      }

      // Check if regions total pins count reached.
      if (regionData.hasOwnProperty(region)){
        if (regionData[region]['pins'].length < regionsCount[region]) {
          regionData[region]['pins'].push(branchData)
        }
      }
    }

    let isSearchLocation = searchParams.region || searchParams.department || searchParams.city;
    if (isSearchLocation && !noResults){
      regionData[activeTab]['zoom'] = "12";
      regionData[activeTab]['lat'] = branchCoord.lat;
      regionData[activeTab]['long'] = branchCoord.long;
      regionData[activeTab]['pins'] = branchCoord.branchPins;
    }

    if (!regionData) {
      return null;
    }

    return (
      <>
        { !isSearchLocation || noResults ? <Tabs key={total} offices={Object.entries(regionData)} activeItem={activeTab ? activeTab : 'métropole'} activeTabCb={setActiveTab}/> : ""}
        <Map
          lat={regionData[activeTab]['lat']}
          long={regionData[activeTab]['long']}
          zoom={regionData[activeTab]['zoom']}
          branchesPins={regionData[activeTab]['pins']}
          apiKey={state.appKeys.google_maps_api_key || ''}
        />
      </>
    )
  }

  if (noResults) {
    noResultsText =
      <div className="wrapper">
        <div className="block__wrapper l:pt-xl l:pb-l">
          <div className="block__header">
            <h2 className="block__title">{intl.formatMessage({id: "NoResults.TextTitle"})}</h2>
          </div>
          <div className="block__content block__content--s body-copy">
            <div>
              { intl.formatMessage({ id: "Branches.NoResultsFirstLine" }) }
            </div>
            <div>
              {
                intl.formatMessage({ id: "Branches.NoResultsSecondLine" }, {
                    link: <a href={match.path.replace(":searchParams*", "")}>{intl.formatMessage({ id: "Branches.NoResultsLinkText" })}</a>
                  }
                )
              }
            </div>
            <br />
            <ul className="list--checks">
              <li>{intl.formatMessage({id: "NoResults.TextListItem1"})}</li>
              <li>{intl.formatMessage({id: "NoResults.TextListItem2"})}</li>
              <li>{intl.formatMessage({id: "NoResults.TextListItem3"})}</li>
            </ul>
          </div>
        </div>
      </div>
  }

  // There two cases where the branch locator pages must be indexable by bots.
  // The first one is when no filters are applied, and this is always true.
  // The second one is when a specialism filter is applied.
  // In any other cases a "noindex" tag is added to the page.
  // REACT_APP_INDEX_SPECIALISM_PAGES variable controls on a high level
  // should we index or not the pages with a specialism filter. It is
  // set to TRUE by default, but it can be overridden by OpCos.
  // In order to have specialism pages indexable, we must have a
  // valid specialism filter, meaning that the specialism search param
  // must exists in the elasticsearch index.
  let indexPage = false;
  if (match.url === match.path.replace(':searchParams*', '')) {
    // The default page with no filters is always indexed.
    indexPage = true;
  } else {
    let isLocation = searchParams.region || searchParams.department || searchParams.city;
    if (!noResults && isLocation) {
      indexPage = true;
    }

    const isSpecialismPage = Object.keys(searchParams).length === 1 && searchParams.specialism !== undefined;
    if (isSpecialismPage && process.env.REACT_APP_INDEX_SPECIALISM_PAGES) {
      let isValidSpecialism = false;
      for (let id in state.specialismOptions) {
        const specialismData = state.specialismOptions[id];
        if (stringSanitizer(specialismData.name) === searchParams.specialism) {
          isValidSpecialism = true;
          break;
        }
      }

      if (isValidSpecialism) {
        indexPage = true;
      }
    }
  }

  const getCurrentLocation = event => {
    event.preventDefault();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function(position) {
        if (position.coords.latitude && position.coords.longitude){
          let lat = position.coords.latitude;
          let lng = position.coords.longitude;

          axios.post('/geodata/current', {
            'lat': lat,
            'lng': lng
          }).then(response => {
            if (response) {
              const {data} = response
              if (typeof data.CityAutocompleteSanitised === "undefined") {
                return;
              }

              let parts = data.CityAutocompleteSanitised.split(', ')
              let locationParts = {
                region: parts[0],
                department: parts[1],
                city: parts[2]
              };

              const params = searchParamsStringify({...searchParams, ...locationParts});
              window.location = `${baseUrl}${params}`;
            }
          }).catch(error => {});
        }
      });
    }
  }

  const metaUrl = `${process.env.REACT_APP_BASE_URL}${baseUrl}${searchParamsStringify(searchParams)}`;
  const metaIndex = indexPage ? "index, follow, max-image-preview:large" : "noindex";
  let pageTitle = intl.formatMessage({ id: "Branches.Title" }, { sitename: process.env.REACT_APP_SITENAME });
  let metaTitle = intl.formatMessage({ id: "Branches.MetaTitle" }, { sitename: process.env.REACT_APP_SITENAME });
  if (searchParams.hasOwnProperty('page') && intl.messages["Global.Page"]) {
    pageTitle = `${intl.formatMessage({id: "Global.Page"}, {page: searchParams.page})} ${pageTitle}`
    metaTitle = `${intl.formatMessage({id: "Global.Page"}, {page: searchParams.page})} ${metaTitle}`
  }
  const metaOgImage = `${process.env.REACT_APP_BASE_URL}${process.env.REACT_APP_META_OG_IMAGE}`;

  return (
    <>
      <Helmet>
        <header className="header header--text bg-variant-brand-quaternary" />
        <title>{pageTitle}</title>
        <link rel="canonical" href={metaUrl} />
        <meta property="og:url" content={metaUrl} />
        <meta name="twitter:url" content={metaUrl} />
        <meta name="title" content={metaTitle} />
        <meta name="twitter:title" content={metaTitle} />
        <meta property="og:title" content={metaTitle} />
        <meta name="description" content={intl.formatMessage({ id: 'Branches.MetaDescription' })} />
        <meta name="twitter:description" content={intl.formatMessage({ id: 'Branches.MetaDescription' })} />
        <meta property="og:description" content={intl.formatMessage({ id: 'Branches.MetaDescription' })} />
        <meta name="og:image" content={metaOgImage} />
        <meta name="twitter:image" content={metaOgImage} />
        <meta name="robots" content={metaIndex} />
      </Helmet>
      <Optimizely optimizelySettings={optimizelySettings} />
      <div className="bg-variant-brand-quaternary -mt-xs l:mt-none">
        <div className="wrapper navigation__wrapper navigation navigation--app">
          <Breadcrumbs breadcrumbs={routeConfig.breadcrumbs} data={state} currentRoute={route}/>
        </div>
      </div>

      <div className="header bg-variant-brand-quaternary">
        <div className="wrapper">
          <div className="header__content header__content--full-width content-block pb-m l:pb-xl" data-rs-input-highlight-wrapper={true}>
            {
              !state.hideElements &&
                <div className="block__header--l">
                  <h1 className="block__title my-none l:mt-m mb-m">{intl.formatMessage({id: "LocationFilter.BlockTitle"})}</h1>
                </div>
            }

            <SearchForm searchParams={searchParams} specialismOptions={state.specialismOptions} setShowAllResults={(state) => setDefaultPage(!state)}/>

            {
              !state.hideElements &&
              <div>
                <div className="location-nearby l:-mb-l" onClick={getCurrentLocation}>
                  <span className="icon icon--inline text-white">
                    <svg>
                      <use xlinkHref="/themes/custom/bluex/dist/assets/image/icons.svg#gps"/>
                    </svg>
                  </span>
                  <span className="currentLocation">
                    <a className="text-white" href="">{intl.formatMessage({id: "LocationFilter.CurrentLocation"})}</a>
                  </span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      {
        (loading && !loadingResult && !showMap && !noResults && !defaultPage)
          ?
          //show loader
          <div className="pb-l l:pb-xl mt-m">
            <div className="wrapper">
              <List />
            </div>
          </div>
          :
          <>
            { noResultsText }
            <div className="bg-brand-tertiary pb-l l:pb-xl">
              <ViewSwitcher resultsTotal={total} searchParams={searchParams} showNoResultsText={defaultPage || noResults}/>
              <div className="wrapper">
                { getContentView() }
                { getMap() }
              </div>
            </div>
          </>
      }
    </>
  );
}

export default injectIntl(Branches);
